import type {
  ApiThresholdDefinitionBundleCreate,
  ApiThresholdDefinitionPatch,
  Source,
} from '@highimpact-frontend/api-types';
import type { AlertColor } from '@mui/material';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import { useEffect, useMemo, useState } from 'react';

// -- type definitions --

export const COORDINATE_SYSTEMS = [
  'time_series',
  'cartesian_2d',
  'polar_2d',
  'cartesian_3d',
  'polar_3d',
] as const;

export type CoordinateSystem = (typeof COORDINATE_SYSTEMS)[number];
export type BoundKind = 'minimum' | 'maximum';
export type RepresentationMode = 'sampled' | 'mesh';

export interface ThresholdSourceBindingItem {
  measure_point_id: number;
  measure_type_id: number;
}

export interface ThresholdDefinitionCreate {
  name: string;
  description: string;
  bound_kind: BoundKind;
  threshold_type: string;
  parameters: Record<string, unknown>;
  evaluation_coordinate_system: CoordinateSystem;
  representation_coordinate_system: CoordinateSystem;
  representation_mode: RepresentationMode;
  active_from: string;
  active_to: string;
}

export interface ThresholdDefinitionBundleAssignment {
  active_from: string;
  active_to: string;
  source_bindings: ThresholdSourceBindingItem[];
}

export interface ThresholdStyleCreate {
  label: string;
  color: string;
  dash: string;
  width: number;
  fill_color: string;
}

export interface ThresholdBundleFormValues {
  definition: ThresholdDefinitionCreate;
  assignment: ThresholdDefinitionBundleAssignment;
  default_style: ThresholdStyleCreate;
}

export interface ThresholdTypesResponseWithItems {
  items: unknown;
}

export interface ThresholdTypesResponseWithTypes {
  types: unknown;
}

export interface ParameterSchemaResponseWithSchema {
  schema: unknown;
}

export interface ThresholdTypeOption {
  id_name: string;
  pretty_name: string;
}

export type ThresholdDefinitionPayload = ApiThresholdDefinitionBundleCreate;
export type ThresholdDefinitionPatchPayload = ApiThresholdDefinitionPatch;
export type ThresholdSubmitPayload =
  | ThresholdDefinitionPayload
  | ThresholdDefinitionPatchPayload;
export type ThresholdFormMode = 'create' | 'edit-definition';

export interface ThresholdBundleFormBaseConfig {
  availableSources?: Record<string, Source[]>;
  allowedCoordinateSystems?: CoordinateSystem[];
  allowedRepresentationModes?: RepresentationMode[];
  submitToApi?: boolean;
}

export interface ThresholdBundleFormCreateConfig
  extends ThresholdBundleFormBaseConfig {
  mode?: 'create';
  thresholdId?: never;
  initialDefinition?: never;
}

export interface ThresholdBundleFormEditDefinitionConfig
  extends ThresholdBundleFormBaseConfig {
  mode: 'edit-definition';
  thresholdId: number;
  initialDefinition?: Partial<ThresholdDefinitionCreate>;
}

export type ThresholdBundleFormConfig =
  | ThresholdBundleFormCreateConfig
  | ThresholdBundleFormEditDefinitionConfig;

export interface ThresholdFormValidationErrors {
  definition_name?: string;
  definition_threshold_type?: string;
  definition_active_period?: string;
  assignment_source_group?: string;
  assignment_period?: string;
  style_color?: string;
  style_fill_color?: string;
  style_width?: string;
}

export interface ThresholdSnackbarState {
  open: boolean;
  severity: AlertColor;
  message: string;
}

export interface ThresholdBundleFormController {
  mode: ThresholdFormMode;
  open: boolean;
  activeStep: number;
  submitError: string | null;
  isSubmitting: boolean;
  lastPayload: ThresholdSubmitPayload | null;
  formValues: ThresholdBundleFormValues;
  thresholdTypeOptions: ThresholdTypeOption[];
  isThresholdTypesLoading: boolean;
  thresholdTypesError: string | null;
  parameterSchema: RJSFSchema | null;
  parameterUiSchema: UiSchema;
  isParameterSchemaLoading: boolean;
  parameterSchemaError: string | null;
  validationErrors: ThresholdFormValidationErrors;
  hasAttemptedConfirmation: boolean;
  snackbar: ThresholdSnackbarState;
  openDialog: () => void;
  closeDialog: () => void;
  setActiveStep: (step: number) => void;
  setDefinitionField: <K extends keyof ThresholdDefinitionCreate>(
    key: K,
    value: ThresholdDefinitionCreate[K]
  ) => void;
  setAssignmentField: <K extends keyof ThresholdDefinitionBundleAssignment>(
    key: K,
    value: ThresholdDefinitionBundleAssignment[K]
  ) => void;
  setStyleField: <K extends keyof ThresholdStyleCreate>(
    key: K,
    value: ThresholdStyleCreate[K]
  ) => void;
  availableSources: Record<string, Source[]>;
  allowedCoordinateSystems: CoordinateSystem[];
  allowedRepresentationModes: RepresentationMode[];
  selectedSourceGroupKeys: string[];
  setSelectedSourceGroupKeys: (keys: string[]) => void;
  validateForConfirmation: () => boolean;
  closeSnackbar: () => void;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

// -- constants --

const INITIAL_FORM_VALUES: ThresholdBundleFormValues = {
  definition: {
    name: '',
    description: '',
    bound_kind: 'maximum',
    threshold_type: '',
    parameters: {},
    evaluation_coordinate_system: 'time_series',
    representation_coordinate_system: 'time_series',
    representation_mode: 'sampled',
    active_from: '',
    active_to: '',
  },
  assignment: {
    active_from: '',
    active_to: '',
    source_bindings: [],
  },
  default_style: {
    label: '',
    color: 'rgba(25, 118, 210, 1)',
    dash: '',
    width: 2,
    fill_color: 'rgba(25, 118, 210, 0.2)',
  },
};

// -- functions --

const clamp = (value: number, min: number, max: number): number => {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, value));
};

const toIsoOrNull = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

const toNullIfEmpty = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const tryParseColorToRgba = (
  value: string
): { r: number; g: number; b: number; a: number } | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1;
      if ([r, g, b, a].every((entry) => Number.isFinite(entry))) {
        return { r, g, b, a };
      }
      return null;
    }

    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
      if ([r, g, b, a].every((entry) => Number.isFinite(entry))) {
        return { r, g, b, a };
      }
      return null;
    }
  }

  const rgbaMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const tokens = rgbaMatch[1].split(',').map((token) => token.trim());
    if (tokens.length === 3 || tokens.length === 4) {
      const r = clamp(Number(tokens[0]), 0, 255);
      const g = clamp(Number(tokens[1]), 0, 255);
      const b = clamp(Number(tokens[2]), 0, 255);
      const a = tokens.length === 4 ? clamp(Number(tokens[3]), 0, 1) : 1;
      if ([r, g, b, a].every((entry) => Number.isFinite(entry))) {
        return { r, g, b, a };
      }
    }
  }

  return null;
};

const rgbaToOpaqueRgbString = (color: {
  r: number;
  g: number;
  b: number;
  a: number;
}): string => {
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const hasOwnEntries = (value: Record<string, unknown>): boolean => {
  return Object.keys(value).length > 0;
};

const readAppLocale = (): string | null => {
  const raw = localStorage.getItem('app_language');
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string' && parsed.trim().length > 0) {
      return parsed;
    }
    return null;
  } catch {
    const normalized = raw.replace(/^"+|"+$/g, '').trim();
    return normalized.length > 0 ? normalized : null;
  }
};

const withLocaleQuery = (endpoint: string, locale: string | null): string => {
  if (!locale) {
    return endpoint;
  }

  return `${endpoint}${
    endpoint.includes('?') ? '&' : '?'
  }locale=${encodeURIComponent(locale)}`;
};

const normalizeThresholdTypeOption = (
  value: unknown
): ThresholdTypeOption | null => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return {
      id_name: value,
      pretty_name: value,
    };
  }

  if (!isPlainObject(value)) {
    return null;
  }

  const idName = value.id_name;
  const prettyName = value.pretty_name;
  if (typeof idName !== 'string' || idName.trim().length === 0) {
    return null;
  }

  return {
    id_name: idName,
    pretty_name:
      typeof prettyName === 'string' && prettyName.trim().length > 0
        ? prettyName
        : idName,
  };
};

const normalizeThresholdTypes = (raw: unknown): ThresholdTypeOption[] => {
  const deduplicated = new Map<string, ThresholdTypeOption>();

  const pushOption = (value: unknown) => {
    const normalized = normalizeThresholdTypeOption(value);
    if (!normalized) {
      return;
    }
    deduplicated.set(normalized.id_name, normalized);
  };

  if (Array.isArray(raw)) {
    raw.forEach(pushOption);
    return [...deduplicated.values()];
  }

  if (raw && typeof raw === 'object') {
    const withItems = raw as ThresholdTypesResponseWithItems;
    if (Array.isArray(withItems.items)) {
      withItems.items.forEach(pushOption);
      return [...deduplicated.values()];
    }

    const withTypes = raw as ThresholdTypesResponseWithTypes;
    if (Array.isArray(withTypes.types)) {
      withTypes.types.forEach((item) => {
        const candidate = normalizeThresholdTypeOption(item);
        if (candidate) {
          deduplicated.set(candidate.id_name, candidate);
          return;
        }

        if (isPlainObject(item) && typeof item.name === 'string') {
          pushOption(item.name);
        }
      });
      return [...deduplicated.values()];
    }
  }

  return [...deduplicated.values()];
};

const normalizeParameterSchema = (raw: unknown): RJSFSchema | null => {
  if (!isPlainObject(raw)) {
    return null;
  }

  const fromWrapped = raw as unknown as ParameterSchemaResponseWithSchema;
  const schemaCandidate = isPlainObject(fromWrapped.schema)
    ? fromWrapped.schema
    : raw;
  if (!isPlainObject(schemaCandidate)) {
    return null;
  }

  const baseType = schemaCandidate.type;
  if (baseType !== undefined && baseType !== 'object') {
    return null;
  }

  return {
    ...schemaCandidate,
    type: 'object',
  } as RJSFSchema;
};

const buildUiSchemaNode = (schemaNode: RJSFSchema | undefined): UiSchema => {
  if (!schemaNode || typeof schemaNode !== 'object') {
    return {};
  }

  if (schemaNode.type === 'array') {
    const arrayItems = buildUiSchemaNode(
      schemaNode.items as RJSFSchema | undefined
    );
    return {
      'ui:options': {
        addable: true,
        removable: true,
        orderable: false,
      },
      ...(Object.keys(arrayItems).length > 0 ? { items: arrayItems } : {}),
    };
  }

  if (schemaNode.type === 'object') {
    const properties = isPlainObject(schemaNode.properties)
      ? (schemaNode.properties as Record<string, RJSFSchema>)
      : {};

    return Object.entries(properties).reduce<UiSchema>(
      (accumulator, [propertyName, propertySchema]) => {
        return {
          ...accumulator,
          [propertyName]: buildUiSchemaNode(propertySchema),
        };
      },
      {}
    );
  }

  return {
    'ui:widget': 'updown',
  };
};

const buildDefaultFormDataNode = (
  schemaNode: RJSFSchema | undefined
): unknown => {
  if (!schemaNode || typeof schemaNode !== 'object') {
    return 0;
  }

  if (schemaNode.default !== undefined) {
    return schemaNode.default;
  }

  if (schemaNode.type === 'array') {
    const itemDefault = buildDefaultFormDataNode(
      schemaNode.items as RJSFSchema | undefined
    );
    return [itemDefault];
  }

  if (schemaNode.type === 'object') {
    const properties = isPlainObject(schemaNode.properties)
      ? (schemaNode.properties as Record<string, RJSFSchema>)
      : {};

    return Object.entries(properties).reduce<Record<string, unknown>>(
      (accumulator, [propertyName, propertySchema]) => {
        return {
          ...accumulator,
          [propertyName]: buildDefaultFormDataNode(propertySchema),
        };
      },
      {}
    );
  }

  return 0;
};

const buildDefaultParameters = (
  schema: RJSFSchema
): Record<string, unknown> => {
  const defaultNode = buildDefaultFormDataNode(schema);
  return isPlainObject(defaultNode) ? defaultNode : {};
};

const buildParametersUiSchema = (schema: RJSFSchema): UiSchema => {
  return buildUiSchemaNode(schema);
};

const buildPayload = (
  values: ThresholdBundleFormValues,
  selectedSourceGroupKeys: readonly string[],
  availableSources: Record<string, Source[]>
): ThresholdDefinitionPayload => {
  const definition = values.definition;
  const style = values.default_style;
  const parsedColor = tryParseColorToRgba(style.color);
  const parsedFillColor = tryParseColorToRgba(style.fill_color);
  const derivedOpacity = parsedFillColor?.a ?? parsedColor?.a ?? null;

  const assignments = selectedSourceGroupKeys
    .map((groupKey) => {
      const sources = availableSources[groupKey] ?? [];
      const source_bindings: ThresholdSourceBindingItem[] = sources.map(
        (source) => ({
          measure_point_id: Number(source.measure_point_id),
          measure_type_id: Number(source.measure_type_id),
        })
      );

      return {
        active_from: toIsoOrNull(values.assignment.active_from),
        active_to: toIsoOrNull(values.assignment.active_to),
        source_bindings,
      };
    })
    .filter((assignment) => assignment.source_bindings.length > 0);

  return {
    definition: {
      name: definition.name,
      description: toNullIfEmpty(definition.description),
      bound_kind: definition.bound_kind,
      threshold_type: definition.threshold_type,
      parameters: definition.parameters,
      evaluation_coordinate_system: definition.evaluation_coordinate_system,
      representation_coordinate_system:
        definition.representation_coordinate_system,
      representation_mode: definition.representation_mode,
      active_from: toIsoOrNull(definition.active_from),
      active_to: toIsoOrNull(definition.active_to),
      is_discarded: false,
    },
    assignments,
    default_style: {
      label: style.label,
      color: parsedColor ? rgbaToOpaqueRgbString(parsedColor) : style.color,
      dash: toNullIfEmpty(style.dash),
      width: Number(style.width),
      fill_color: parsedFillColor
        ? rgbaToOpaqueRgbString(parsedFillColor)
        : toNullIfEmpty(style.fill_color),
      opacity:
        derivedOpacity === null
          ? null
          : Math.round(clamp(derivedOpacity, 0, 1) * 100) / 100,
    },
  };
};

const getAuthorizationHeader = (): string | null => {
  const token = sessionStorage.getItem('security_token');
  return token ? `Bearer ${token}` : null;
};

const createEmptyState = (): ThresholdBundleFormValues => {
  return {
    ...INITIAL_FORM_VALUES,
    definition: {
      ...INITIAL_FORM_VALUES.definition,
      parameters: {},
    },
  };
};

const applyInitialDefinition = (
  definition: ThresholdDefinitionCreate,
  initialDefinition?: Partial<ThresholdDefinitionCreate>
): ThresholdDefinitionCreate => {
  if (!initialDefinition) {
    return definition;
  }

  return {
    ...definition,
    ...initialDefinition,
    name: initialDefinition.name ?? definition.name,
    description: initialDefinition.description ?? definition.description,
    threshold_type:
      initialDefinition.threshold_type ?? definition.threshold_type,
    parameters: initialDefinition.parameters ?? definition.parameters,
    active_from: initialDefinition.active_from ?? definition.active_from,
    active_to: initialDefinition.active_to ?? definition.active_to,
  };
};

const buildPatchPayload = (
  values: ThresholdBundleFormValues
): ThresholdDefinitionPatchPayload => {
  const definition = values.definition;
  return {
    name: definition.name,
    description: toNullIfEmpty(definition.description),
    bound_kind: definition.bound_kind,
    threshold_type: definition.threshold_type,
    parameters: definition.parameters,
    evaluation_coordinate_system: definition.evaluation_coordinate_system,
    representation_coordinate_system:
      definition.representation_coordinate_system,
    representation_mode: definition.representation_mode,
    active_from: toIsoOrNull(definition.active_from),
    active_to: toIsoOrNull(definition.active_to),
    is_discarded: false,
  };
};

const generateStyleLabel = (): string => {
  return `style_${Date.now()}`;
};

const isValidColor = (value: string): boolean => {
  return tryParseColorToRgba(value) !== null;
};

const buildValidationErrors = (
  values: ThresholdBundleFormValues,
  selectedSourceGroupKeys: readonly string[],
  mode: ThresholdFormMode
): ThresholdFormValidationErrors => {
  const errors: ThresholdFormValidationErrors = {};

  if (!values.definition.name.trim()) {
    errors.definition_name = 'Le nom du seuil est requis.';
  }

  if (!values.definition.threshold_type.trim()) {
    errors.definition_threshold_type = 'Le type de seuil est requis.';
  }

  if (mode === 'create' && selectedSourceGroupKeys.length === 0) {
    errors.assignment_source_group =
      "Veuillez sélectionner au moins un groupe d'assignation.";
  }

  if (mode === 'edit-definition') {
    return errors;
  }

  if (!isValidColor(values.default_style.color)) {
    errors.style_color = 'La couleur de ligne est invalide.';
  }

  if (!isValidColor(values.default_style.fill_color)) {
    errors.style_fill_color = 'La couleur de remplissage est invalide.';
  }

  if (!(Number(values.default_style.width) > 0)) {
    errors.style_width = "L'épaisseur doit être supérieure à 0.";
  }

  return errors;
};

// -- main hook --

export function useThresholdBundleFormController(
  config: ThresholdBundleFormConfig = {}
): ThresholdBundleFormController {
  const mode: ThresholdFormMode =
    config.mode === 'edit-definition' ? 'edit-definition' : 'create';
  const thresholdId =
    config.mode === 'edit-definition' ? config.thresholdId : undefined;
  const initialDefinition =
    config.mode === 'edit-definition' ? config.initialDefinition : undefined;

  const resolvedAvailableSources = useMemo(
    () => config.availableSources ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(config.availableSources)]
  );

  const resolvedCoordinateSystems = useMemo(
    () =>
      config.allowedCoordinateSystems &&
      config.allowedCoordinateSystems.length > 0
        ? config.allowedCoordinateSystems
        : [...COORDINATE_SYSTEMS],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(config.allowedCoordinateSystems)]
  );

  const resolvedRepresentationModes = useMemo(
    () =>
      config.allowedRepresentationModes &&
      config.allowedRepresentationModes.length > 0
        ? config.allowedRepresentationModes
        : (['sampled', 'mesh'] as RepresentationMode[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(config.allowedRepresentationModes)]
  );

  const submitToApi = config.submitToApi ?? false;
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastPayload, setLastPayload] = useState<ThresholdSubmitPayload | null>(
    null
  );
  const [formValues, setFormValues] =
    useState<ThresholdBundleFormValues>(INITIAL_FORM_VALUES);
  const [thresholdTypeOptions, setThresholdTypeOptions] = useState<
    ThresholdTypeOption[]
  >([]);
  const [isThresholdTypesLoading, setIsThresholdTypesLoading] = useState(false);
  const [thresholdTypesError, setThresholdTypesError] = useState<string | null>(
    null
  );
  const [parameterSchema, setParameterSchema] = useState<RJSFSchema | null>(
    null
  );
  const [parameterUiSchema, setParameterUiSchema] = useState<UiSchema>({});
  const [isParameterSchemaLoading, setIsParameterSchemaLoading] =
    useState(false);
  const [parameterSchemaError, setParameterSchemaError] = useState<
    string | null
  >(null);

  const [selectedSourceGroupKeys, setSelectedSourceGroupKeysState] = useState<
    string[]
  >([]);
  const [hasAttemptedConfirmation, setHasAttemptedConfirmation] =
    useState(false);
  const [validationErrors, setValidationErrors] =
    useState<ThresholdFormValidationErrors>({});
  const [snackbar, setSnackbar] = useState<ThresholdSnackbarState>({
    open: false,
    severity: 'info',
    message: '',
  });

  const isDirty =
    JSON.stringify(formValues) !== JSON.stringify(createEmptyState());

  useEffect(() => {
    if (!open || thresholdTypeOptions.length > 0) {
      return;
    }

    const controller = new AbortController();

    const loadThresholdTypes = async () => {
      try {
        setIsThresholdTypesLoading(true);
        setThresholdTypesError(null);
        const authorization = getAuthorizationHeader();
        const locale = readAppLocale();
        const response = await fetch(
          withLocaleQuery('/api/config/thresholds/definitions/types', locale),
          {
            method: 'GET',
            headers: {
              ...(authorization ? { Authorization: authorization } : {}),
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const json = (await response.json()) as unknown;
        setThresholdTypeOptions(normalizeThresholdTypes(json));
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        console.error('Erreur de chargement des types de seuils', error);
        setThresholdTypesError(
          'Impossible de charger les types de seuil depuis /api/config/thresholds/definitions/types.'
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsThresholdTypesLoading(false);
        }
      }
    };

    void loadThresholdTypes();

    return () => {
      controller.abort();
    };
  }, [open, thresholdTypeOptions.length]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const thresholdType = formValues.definition.threshold_type.trim();
    if (!thresholdType) {
      setParameterSchema(null);
      setParameterUiSchema({});
      setParameterSchemaError(null);
      setFormValues((previous) => ({
        ...previous,
        definition: {
          ...previous.definition,
          parameters: {},
        },
      }));
      return;
    }

    const controller = new AbortController();

    const loadParameterSchema = async () => {
      try {
        setIsParameterSchemaLoading(true);
        setParameterSchemaError(null);

        const authorization = getAuthorizationHeader();
        const locale = readAppLocale();
        const endpoint = `/api/config/thresholds/definitions/types/${encodeURIComponent(
          thresholdType
        )}/parameter-schema`;
        const response = await fetch(withLocaleQuery(endpoint, locale), {
          method: 'GET',
          headers: {
            ...(authorization ? { Authorization: authorization } : {}),
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const json = (await response.json()) as unknown;
        const normalizedSchema = normalizeParameterSchema(json);
        if (!normalizedSchema) {
          throw new Error('Invalid JSON Schema payload');
        }

        setParameterSchema(normalizedSchema);
        setParameterUiSchema(buildParametersUiSchema(normalizedSchema));
        setFormValues((previous) => ({
          ...previous,
          definition: {
            ...previous.definition,
            parameters: hasOwnEntries(previous.definition.parameters)
              ? previous.definition.parameters
              : buildDefaultParameters(normalizedSchema),
          },
        }));
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        console.error('Erreur de chargement du schema des paramètres', error);
        setParameterSchema(null);
        setParameterUiSchema({});
        setParameterSchemaError(
          'Impossible de charger le schéma des paramètres pour ce type de seuil.'
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsParameterSchemaLoading(false);
        }
      }
    };

    void loadParameterSchema();

    return () => {
      controller.abort();
    };
  }, [open, formValues.definition.threshold_type]);

  useEffect(() => {
    if (!hasAttemptedConfirmation) {
      return;
    }

    setValidationErrors(
      buildValidationErrors(formValues, selectedSourceGroupKeys, mode)
    );
  }, [formValues, hasAttemptedConfirmation, selectedSourceGroupKeys, mode]);

  const setDefinitionField = <K extends keyof ThresholdDefinitionCreate>(
    key: K,
    value: ThresholdDefinitionCreate[K]
  ) => {
    setFormValues((previous) => ({
      ...previous,
      definition: {
        ...previous.definition,
        [key]: value,
      },
    }));
  };

  const setAssignmentField = <
    K extends keyof ThresholdDefinitionBundleAssignment
  >(
    key: K,
    value: ThresholdDefinitionBundleAssignment[K]
  ) => {
    setFormValues((previous) => ({
      ...previous,
      assignment: {
        ...previous.assignment,
        [key]: value,
      },
    }));
  };

  const setStyleField = <K extends keyof ThresholdStyleCreate>(
    key: K,
    value: ThresholdStyleCreate[K]
  ) => {
    setFormValues((previous) => ({
      ...previous,
      default_style: {
        ...previous.default_style,
        [key]: value,
      },
    }));
  };

  const setSelectedSourceGroupKeys = (keys: string[]) => {
    setSelectedSourceGroupKeysState([...keys]);

    const bindingsByKey = new Map<string, ThresholdSourceBindingItem>();
    keys.forEach((key) => {
      const sources = resolvedAvailableSources[key] ?? [];
      sources.forEach((source) => {
        const bindingKey = `${source.measure_point_id}-${source.measure_type_id}`;
        bindingsByKey.set(bindingKey, {
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        });
      });
    });

    const bindings = Array.from(bindingsByKey.values());

    setFormValues((previous) => ({
      ...previous,
      assignment: {
        ...previous.assignment,
        source_bindings: bindings,
      },
    }));
  };

  const validateForConfirmation = (): boolean => {
    const nextErrors = buildValidationErrors(
      formValues,
      selectedSourceGroupKeys,
      mode
    );
    setHasAttemptedConfirmation(true);
    setValidationErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Certains champs obligatoires sont manquants ou invalides.',
      });
      return false;
    }

    return true;
  };

  const closeSnackbar = () => {
    setSnackbar((previous) => ({ ...previous, open: false }));
  };

  const openDialog = () => {
    const emptyState = createEmptyState();
    let nextDefinition = emptyState.definition;
    if (resolvedCoordinateSystems.length === 1) {
      nextDefinition = {
        ...nextDefinition,
        evaluation_coordinate_system: resolvedCoordinateSystems[0],
        representation_coordinate_system: resolvedCoordinateSystems[0],
      };
    }
    if (resolvedRepresentationModes.length === 1) {
      nextDefinition = {
        ...nextDefinition,
        representation_mode: resolvedRepresentationModes[0],
      };
    }
    nextDefinition = applyInitialDefinition(nextDefinition, initialDefinition);
    setFormValues({
      ...emptyState,
      definition: nextDefinition,
      default_style: {
        ...emptyState.default_style,
        label: generateStyleLabel(),
      },
    });
    setSelectedSourceGroupKeysState([]);
    setHasAttemptedConfirmation(false);
    setValidationErrors({});
    setSnackbar((previous) => ({ ...previous, open: false }));
    setOpen(true);
  };

  const closeDialog = () => {
    if (isSubmitting) {
      return;
    }
    if (
      isDirty &&
      !window.confirm(
        'Des modifications non enregistrées seront perdues. Fermer quand même ?'
      )
    ) {
      return;
    }

    setOpen(false);
    setActiveStep(0);
    setSubmitError(null);
    setFormValues(createEmptyState());
    setSelectedSourceGroupKeysState([]);
    setHasAttemptedConfirmation(false);
    setValidationErrors({});
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForConfirmation()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload: ThresholdSubmitPayload =
        mode === 'edit-definition'
          ? buildPatchPayload(formValues)
          : buildPayload(
              formValues,
              selectedSourceGroupKeys,
              resolvedAvailableSources
            );

      if (submitToApi) {
        const authorization = getAuthorizationHeader();
        const endpoint =
          mode === 'edit-definition'
            ? thresholdId === undefined
              ? null
              : `/api/config/thresholds/definitions/${thresholdId}`
            : '/api/config/thresholds/definitions/bundle';

        if (!endpoint) {
          throw new Error('Missing thresholdId for edit-definition mode');
        }

        const response = await fetch(endpoint, {
          method: mode === 'edit-definition' ? 'PATCH' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authorization ? { Authorization: authorization } : {}),
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } else {
        await new Promise((resolve) => setTimeout(resolve, 900));
      }
      setLastPayload(payload);
      setOpen(false);
      setActiveStep(0);
      setFormValues(createEmptyState());
      setSelectedSourceGroupKeysState([]);
      setHasAttemptedConfirmation(false);
      setValidationErrors({});
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          mode === 'edit-definition'
            ? 'Le seuil a été modifié avec succès.'
            : 'Le seuil a été créé avec succès.',
      });
    } catch {
      setSubmitError(
        mode === 'edit-definition'
          ? 'La modification du seuil a échoué. Veuillez réessayer.'
          : 'La création du seuil a échoué. Veuillez réessayer.'
      );
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          mode === 'edit-definition'
            ? 'La modification du seuil a échoué.'
            : 'La création du seuil a échoué.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    mode,
    open,
    activeStep,
    submitError,
    isSubmitting,
    lastPayload,
    formValues,
    thresholdTypeOptions,
    isThresholdTypesLoading,
    thresholdTypesError,
    parameterSchema,
    parameterUiSchema,
    isParameterSchemaLoading,
    parameterSchemaError,
    validationErrors,
    hasAttemptedConfirmation,
    snackbar,
    openDialog,
    closeDialog,
    setActiveStep,
    setDefinitionField,
    setAssignmentField,
    setStyleField,
    availableSources: resolvedAvailableSources,
    allowedCoordinateSystems: resolvedCoordinateSystems,
    allowedRepresentationModes: resolvedRepresentationModes,
    selectedSourceGroupKeys,
    setSelectedSourceGroupKeys,
    validateForConfirmation,
    closeSnackbar,
    handleSubmit,
  };
}
